/**
 * The colour of a Speedy's phone screen and topic ring (Tim, 3 Nov: the phone card with the topic
 * colour and the lighter sun). One colour per topic (the 8 build parts and 4 pillars of the
 * `groups` list), and one per series colour. Dark gradients so white text reads on every one.
 */
import type { SpeedySeriesTile } from '@/api/speedies';

export interface SpeedyColour {
  /** The phone screen. */
  screen: string;
  /** The phones behind a series. */
  back: string;
  /** The topic ring. */
  ring: string;
}

const C = {
  green: { screen: 'linear-gradient(160deg, #0c3a24 0%, #1a6a3a 100%)', back: '#12482c', ring: '#5fc488' },
  rust: { screen: 'linear-gradient(160deg, #4d1a0a 0%, #a3401b 100%)', back: '#7a3014', ring: '#ec8a5c' },
  red: { screen: 'linear-gradient(160deg, #4f0d0f 0%, #a3191d 100%)', back: '#7a1316', ring: '#ef6a6e' },
  teal: { screen: 'linear-gradient(160deg, #0b3442 0%, #1b6378 100%)', back: '#135062', ring: '#5cc2dc' },
  purple: { screen: 'linear-gradient(160deg, #331585 0%, #6d2ad6 100%)', back: '#4f1fa8', ring: '#b18cff' },
  gold: { screen: 'linear-gradient(160deg, #4a3608 0%, #a07a1c 100%)', back: '#6e5410', ring: '#e8c46a' },
  slate: { screen: 'linear-gradient(160deg, #1f2733 0%, #3b4a5e 100%)', back: '#2c3747', ring: '#9fb3cc' },
  olive: { screen: 'linear-gradient(160deg, #2a3410 0%, #5a6e1e 100%)', back: '#3e4c16', ring: '#b8d36a' },
  plum: { screen: 'linear-gradient(160deg, #3d0f33 0%, #86266f 100%)', back: '#5e1a4f', ring: '#e486cc' },
  sky: { screen: 'linear-gradient(160deg, #0d2c4f 0%, #1d5fa3 100%)', back: '#16427a', ring: '#7fb8f0' },
  terracotta: { screen: 'linear-gradient(160deg, #4a1f12 0%, #b4552f 100%)', back: '#7d3a20', ring: '#e8936c' },
  stone: { screen: 'linear-gradient(160deg, #2b2925 0%, #57524a 100%)', back: '#3d3a35', ring: '#c9c2b6' },
} satisfies Record<string, SpeedyColour>;

/** By topic key (`groups` list, migration 697). */
const TOPIC: Record<string, SpeedyColour> = {
  project_preliminaries: C.slate,
  site_preparation: C.olive,
  substructure: C.stone,
  structure: C.teal,
  building_envelope: C.rust,
  services: C.sky,
  interior_finishing: C.plum,
  external_works: C.green,
  culture: C.purple,
  finance: C.terracotta,
  management: C.red,
  marketing: C.gold,
};

export function topicColour(groupKey: string | null | undefined): SpeedyColour {
  return (groupKey && TOPIC[groupKey]) || C.stone;
}

const SERIES: Record<SpeedySeriesTile['color'], SpeedyColour> = {
  teal: C.teal,
  amber: C.gold,
  rose: C.red,
  violet: C.purple,
  sky: C.sky,
  lime: C.olive,
};

export function seriesColour(color: SpeedySeriesTile['color']): SpeedyColour {
  return SERIES[color] ?? C.stone;
}
